/**
 * Die Kennzahlen, die im gedruckten Buch stehen.
 *
 * Gerechnet wird nichts Neues: Kilometer, Stunden und Kraftstoff kommen aus
 * tripTotals, die Ausgaben aus totalSpent, der Abgleich mit den berechneten
 * Strecken aus routeComparison. Diese Datei stellt sie nur zusammen und
 * ergänzt, was nur der Druck braucht - die Länderfolge, die Zahl der Fotos
 * und den Zeitraum der Reise.
 *
 * Warum überhaupt eine eigene Funktion, wenn drei bestehende zusammengefasst
 * werden: die Kennzahlenseite ist die einzige Stelle im Buch, an der alle Zahlen
 * nebeneinander stehen, und wenn zwei davon nicht zueinander passen, fällt es
 * genau dort auf. Also gibt es einen Aufruf, der alle liefert, und einen Test,
 * der sie gemeinsam prüft.
 */

import { parseIsoDay, travelDayCount, travelDayDate } from '../../lib/calendar'
import { activeStages, routeComparison, totalSpent, tripTotals } from '../../lib/trip'
import type { DiaryEntry, Expense, LatLon, PhotoMeta, Stage, Trip } from '../../lib/types'

export interface PrintMetrics {
  /** Etappen, die gefahren werden sollen. Ausgelassene zählen nicht mit. */
  stageCount: number
  /** Etappen, die im Tagebuch abgehakt sind. */
  drivenCount: number
  /** Summe der geplanten Kilometer. */
  plannedKm: number
  /** Summe der tatsächlich gefahrenen Kilometer. */
  drivenKm: number
  /** Reine Fahrzeit der Planung in Stunden. */
  hours: number
  /** Was der Routendienst für die berechneten Etappen ausweist. */
  routedKm: number
  /** Die Länder in der Reihenfolge der Reise, jedes einmal. */
  countries: string[]
  /** Fotos im ganzen Buch. */
  photoCount: number
  /** Summe aller erfassten Ausgaben. */
  spent: number
  /** Geschätzte Kraftstoffkosten der geplanten Strecke. */
  fuelCost: number
  /** Erster und letzter Reisetag als ISO-Datum, oder null ohne Startdatum. */
  period: { from: string; to: string } | null
}

/**
 * Trennzeichen zwischen zwei Ländern im Feld land einer Etappe.
 *
 * Dort steht entweder ein Kürzel ("SI") oder ein Übergang ("HR→BA"). Der
 * Pfeil ist das Zeichen aus der Startbestückung, der Schrägstrich und die
 * beiden ASCII-Pfeile stehen daneben, weil die Etappen von Hand bearbeitet
 * werden und niemand auf einer Telefontastatur einen Pfeil sucht.
 */
const LAND_TRENNER = /→|->|=>|\//

/**
 * Die Länder der Reise, in ihrer Reihenfolge und ohne Wiederholung.
 *
 * Die Reihenfolge ist die Aussage: "CH SI HR BA ME RS BG RO HU" liest sich wie
 * die Route. Alphabetisch sortiert wäre dieselbe Menge und die halbe Auskunft.
 * Ein Land, das auf dem Rückweg noch einmal vorkommt, steht deshalb an der
 * Stelle seines ersten Auftretens und nicht zweimal.
 */
export function countriesOf(stages: readonly Stage[]): string[] {
  const gesehen = new Set<string>()
  const liste: string[] = []

  for (const stage of stages) {
    for (const teil of stage.land.split(LAND_TRENNER)) {
      const land = teil.trim()
      if (!land || gesehen.has(land)) continue
      gesehen.add(land)
      liste.push(land)
    }
  }
  return liste
}

/**
 * Die Route als eine Zeile für die Titelseite.
 *
 * "Bern → Bukarest → Bern" und nicht "Bern → Bern": diese Reise fährt im Kreis,
 * und der blosse Anfang und das blosse Ende sagen dann gar nichts. Genannt wird
 * deshalb zusätzlich der Wendepunkt - das Ziel, das vom Start am weitesten weg
 * liegt. Bei einer Reise, die woanders endet als sie beginnt, bleibt es bei den
 * beiden Enden; dort ist die Aussage schon vollständig.
 *
 * Als Wendepunkt kommen die Etappenziele in Frage und dazu die benannten
 * Zwischenziele. Ohne sie stünde über einer Reise, die aus einer einzigen Runde
 * besteht - Zuhause über Zofingen, Olten und Aarau nach Hause -, nur „Zuhause“:
 * das einzige Etappenziel dieser Reise ist ihr Start, und der Abstand zu sich
 * selbst ist null. Unbenannte Zwischenziele bleiben aussen vor, denn ein
 * Wendepunkt ohne Namen ist keine Zeile für eine Titelseite.
 *
 * Gemessen wird in Gradquadraten und nicht in Kilometern: für ein Grösser-oder-
 * kleiner braucht es keine Erdkrümmung, und die Wurzel spart man sich auch.
 */
export function routeLabel(stages: readonly Stage[]): string {
  const erste = stages[0]
  const letzte = stages.at(-1)
  if (!erste || !letzte) return ''
  if (erste.from !== letzte.to) return `${erste.from} → ${letzte.to}`

  let wende = ''
  let weiteste = 0
  const pruefe = (at: LatLon, name: string) => {
    const dLat = at[0] - erste.c1[0]
    const dLon = at[1] - erste.c1[1]
    const abstand = dLat * dLat + dLon * dLon
    if (abstand > weiteste) {
      weiteste = abstand
      wende = name
    }
  }

  for (const stage of stages) {
    for (const punkt of stage.via ?? []) {
      if (punkt.name.trim() !== '') pruefe(punkt.at, punkt.name)
    }
    pruefe(stage.c2, stage.to)
  }

  return wende ? `${erste.from} → ${wende} → ${letzte.to}` : erste.from
}

/**
 * Erster und letzter Tag der Reise.
 *
 * Eine Etappe ist ein Tag, der letzte Tag also der Start plus die Zahl der
 * Reisetage minus eins. Gerechnet wird das in lib/calendar.ts, und zwar mit
 * derselben Funktion, die auch das Datum einer einzelnen Etappe bestimmt: der
 * Umschlag und die Einsortierung der GPX-Dateien reden über dieselbe Reise, und
 * sie dürfen nicht zwei verschiedene Enden ausrechnen.
 *
 * @param dayCount Zahl der Reisetage, also travelDayCount - ausgelassene
 *   Etappen zählen nicht mit.
 * @returns null, wenn kein brauchbares Startdatum eingetragen ist oder die
 *   Reise noch keinen Tag hat. Dann steht auf der Titelseite kein Zeitraum
 *   statt eines erfundenen - null Etappen ergaben vorher eine Reise von genau
 *   einem Tag, und die stand als "17. August 2026 – 17. August 2026" auf dem
 *   Umschlag.
 */
export function tripPeriod(startIso: string, dayCount: number): PrintMetrics['period'] {
  const bis = travelDayDate(startIso, dayCount)
  if (!bis) return null
  return { from: startIso, to: bis }
}

const MONATE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
]

/**
 * Ein ISO-Datum als deutscher Text, etwa "17. August 2026".
 *
 * Von Hand gesetzt statt über Intl: die Monatsnamen sollen im Buch immer
 * gleich aussehen, unabhängig davon, welche Sprachdaten der Browser
 * mitliefert - und ein Test, der Intl prüft, prüft die Laufzeitumgebung.
 *
 * @param kurz Nur Tag und Monat, ohne Jahr. Für den Zeitraum, wenn beide Enden
 *   im selben Jahr liegen und das Jahr nur einmal dastehen soll.
 */
export function germanDate(iso: string, kurz = false): string {
  const datum = parseIso(iso)
  if (!datum) return iso
  const monat = MONATE[datum.getUTCMonth()] ?? ''
  const tag = `${String(datum.getUTCDate())}. ${monat}`
  return kurz ? tag : `${tag} ${String(datum.getUTCFullYear())}`
}

/** Der Zeitraum als eine Zeile, etwa "17. August – 1. September 2026". */
export function periodLabel(period: PrintMetrics['period']): string {
  if (!period) return ''
  const von = parseIso(period.from)
  const bis = parseIso(period.to)
  if (!von || !bis) return ''
  const gleichesJahr = von.getUTCFullYear() === bis.getUTCFullYear()
  return `${germanDate(period.from, gleichesJahr)} – ${germanDate(period.to)}`
}

/** Wochentag und Datum für die Kopfzeile einer Etappe, etwa "Mo, 17. August". */
const WOCHENTAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']

export function germanWeekday(iso: string): string {
  const datum = parseIso(iso)
  if (!datum) return ''
  return WOCHENTAGE[datum.getUTCDay()] ?? ''
}

/**
 * Gelesen wird über lib/calendar und nicht hier: die Regel, welche Zeichenkette
 * ein Datum ist, gilt für das Buch und für die Reise gleich. Hier stand die
 * gleiche Prüfung ein zweites Mal und nahm dabei den 30. Februar an - im
 * gedruckten Buch stand dann der 2. März, samt passendem Wochentag.
 */
function parseIso(iso: string): Date | null {
  const zeit = parseIsoDay(iso)
  return zeit === null ? null : new Date(zeit)
}

export interface MetricsInput {
  trip: Trip
  diary: Record<string, DiaryEntry>
  expenses: readonly Expense[]
  photosByStage: Record<string, PhotoMeta[]>
}

/** Stellt alle Zahlen der Kennzahlenseite zusammen. */
export function printMetrics(input: MetricsInput): PrintMetrics {
  const stages = activeStages(input.trip)
  const totals = tripTotals(input.trip, input.diary)
  const strecken = routeComparison(stages)

  let photoCount = 0
  for (const stage of stages) {
    photoCount += input.photosByStage[stage.id]?.length ?? 0
  }

  return {
    stageCount: totals.stageCount,
    drivenCount: totals.drivenCount,
    plannedKm: totals.km,
    drivenKm: totals.drivenKm,
    hours: totals.hours,
    routedKm: strecken.routedKm,
    countries: countriesOf(stages),
    photoCount,
    spent: totalSpent([...input.expenses]),
    fuelCost: totals.fuelCost,
    // Nicht totals.stageCount, obwohl dieselbe Zahl herauskommt: das ist die
    // Zahl der Etappen, die in die Summen gehen, und hier geht es um Tage.
    period: tripPeriod(input.trip.meta.start, travelDayCount(input.trip.stages)),
  }
}
